/**
 * RequirementCatalog.jsx — REQUIREMENT-FOUNDATION-001 · Phase 3
 *
 * Provider Admin Requirement Definition catalog.
 *
 * Grid-first, dense rows. Uses journey-ux primitives. NOT a coordinator
 * or patient workspace — this is the foundation authoring surface only.
 *
 * Endpoints:
 *   GET    /api/clinic/requirements
 *   POST   /api/clinic/requirements
 *   PUT    /api/clinic/requirements/{requirement_id}
 *   POST   /api/clinic/requirements/{requirement_id}/archive
 *   POST   /api/clinic/requirements/{requirement_id}/restore
 *   GET    /api/clinic/requirements/{requirement_id}/versions
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { formatDateTimeLocale } from '../../../utils/dateFormat';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api';
import { Pill, FilterChips, DataGrid, DetailDrawer, DrawerField } from '../../../components/journey-ux';
import { toast } from 'sonner';

const TYPE_BADGE = {
  DOCUMENT: 'sky',
  CONSENT: 'violet',
  PATIENT_ACTION: 'amber',
  TASK: 'slate',
};

const EMPTY_DRAFT = { type: 'DOCUMENT', title: '', description: '' };

export default function RequirementCatalog() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [versionList, setVersionList] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/clinic/requirements`, {
        params: { include_archived: includeArchived },
      });
      if (res.data?.success) {
        setItems(res.data.items || []);
        setCount(res.data.count || 0);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || t('provider.reqCatalog.toastFailedLoad'));
    } finally {
      setLoading(false);
    }
  }, [includeArchived]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter((r) => r.type === filter);
  }, [items, filter]);

  const selected = useMemo(
    () => items.find((r) => r.requirement_id === selectedId) || null,
    [items, selectedId],
  );

  // ── Filter chips with counts ──────────────────────────────────────
  const chips = useMemo(() => {
    const by = items.reduce((acc, r) => ((acc[r.type] = (acc[r.type] || 0) + 1), acc), {});
    return [
      { id: 'all', label: t('provider.reqCatalog.chipAll', { count: items.length }) },
      { id: 'DOCUMENT', label: t('provider.reqCatalog.chipDocument', { count: by.DOCUMENT || 0 }) },
      { id: 'CONSENT', label: t('provider.reqCatalog.chipConsent', { count: by.CONSENT || 0 }) },
      { id: 'PATIENT_ACTION', label: t('provider.reqCatalog.chipPatientAction', { count: by.PATIENT_ACTION || 0 }) },
      { id: 'TASK', label: t('provider.reqCatalog.chipTask', { count: by.TASK || 0 }) },
    ];
  }, [items]);

  // ── DataGrid columns ──────────────────────────────────────────────
  const columns = useMemo(() => ([
    { id: 'title', header: t('provider.reqCatalog.colTitle'), render: (r) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-800">{r.title}</span>
          {r.archived && <Pill tone="rose" label={t('provider.reqCatalog.archivedPill')} />}
        </div>
      ) },
    { id: 'type', header: t('provider.reqCatalog.colType'), render: (r) => <Pill tone={TYPE_BADGE[r.type] || 'slate'} label={r.type} /> },
    { id: 'version', header: t('provider.reqCatalog.colVer'), align: 'right', render: (r) => `v${r.version}` },
    { id: 'description', header: t('provider.reqCatalog.colDescription'), render: (r) => (
        <span className="text-slate-600 line-clamp-1">{r.description || '—'}</span>
      ) },
    { id: 'updated_at', header: t('provider.reqCatalog.colUpdated'), render: (r) => formatDateTimeLocale(r.updated_at) },
  ]), [t]);

  // ── Save (create + update) ────────────────────────────────────────
  const save = async () => {
    if (!draft.title?.trim()) return toast.error(t('provider.reqCatalog.toastTitleReq'));
    setSaving(true);
    try {
      if (draft.requirement_id) {
        const { data } = await api.put(
          `/clinic/requirements/${draft.requirement_id}`,
          { type: draft.type, title: draft.title, description: draft.description },
        );
        if (data?.success) {
          toast.success(t('provider.reqCatalog.toastUpdatedTo', { v: data.requirement.version }));
          setDraft(null);
          await load();
        }
      } else {
        const { data } = await api.post(
          `/clinic/requirements`,
          { type: draft.type, title: draft.title, description: draft.description },
        );
        if (data?.success) {
          toast.success(t('provider.reqCatalog.toastCreatedV1'));
          setDraft(null);
          await load();
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || t('provider.reqCatalog.toastSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const archive = async (rid, restore = false) => {
    try {
      const path = restore ? 'restore' : 'archive';
      const { data } = await api.post(`/clinic/requirements/${rid}/${path}`, {});
      if (data?.success) {
        toast.success(restore ? t('provider.reqCatalog.toastRestored') : t('provider.reqCatalog.toastArchived'));
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || t('provider.reqCatalog.toastActionFailed'));
    }
  };

  const showVersions = async (rid) => {
    try {
      const { data } = await api.get(`/clinic/requirements/${rid}/versions`);
      if (data?.success) setVersionList(data.versions || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || t('provider.reqCatalog.toastFailedVersions'));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="requirement-catalog-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-20" data-testid="catalog-header">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">{t('provider.reqCatalog.pageTitle')}</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {t('provider.reqCatalog.pageSubtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-600 flex items-center gap-1" data-testid="toggle-archived">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                className="rounded border-slate-300"
                data-testid="toggle-archived-input"
              />
              {t('provider.reqCatalog.showArchived')}
            </label>
            <button
              onClick={() => setDraft({ ...EMPTY_DRAFT })}
              className="px-3 py-1.5 text-xs font-medium rounded bg-slate-900 text-white hover:bg-slate-800"
              data-testid="catalog-new-btn"
            >
              {t('provider.reqCatalog.newRequirement')}
            </button>
          </div>
        </div>
        <div className="mt-3" data-testid="catalog-filters">
          <FilterChips chips={chips} activeId={filter} onChange={setFilter} testId="catalog-filter" />
        </div>
      </div>

      {/* Grid */}
      <div className="px-6 pt-3" data-testid="catalog-grid-wrap">
        <DataGrid
          columns={columns}
          rows={filtered}
          rowKey={(r) => r.requirement_id}
          onRowClick={(r) => setSelectedId(r.requirement_id)}
          activeRowId={selectedId}
          selectable={false}
          loading={loading}
          emptyMessage={
            count === 0
              ? t('provider.reqCatalog.emptyNone')
              : t('provider.reqCatalog.emptyFilter')
          }
          testId="catalog-grid"
        />
      </div>

      {/* Detail drawer */}
      <DetailDrawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.title || ''}
        eyebrow={selected ? t('provider.reqCatalog.drawerEyebrow', { type: selected.type, version: selected.version }) : ''}
        testId="catalog-drawer"
        footer={
          selected ? (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setDraft({ ...selected })}
                className="px-2.5 py-1 text-xs rounded border border-slate-300 hover:bg-slate-50"
                data-testid="drawer-edit-btn"
              >
                {t('provider.reqCatalog.drawerEdit')}
              </button>
              <button
                onClick={() => showVersions(selected.requirement_id)}
                className="px-2.5 py-1 text-xs rounded border border-slate-300 hover:bg-slate-50"
                data-testid="drawer-versions-btn"
              >
                {t('provider.reqCatalog.drawerHistory')}
              </button>
              {selected.archived ? (
                <button
                  onClick={() => archive(selected.requirement_id, true)}
                  className="px-2.5 py-1 text-xs rounded bg-emerald-600 text-white"
                  data-testid="drawer-restore-btn"
                >
                  {t('provider.reqCatalog.drawerRestore')}
                </button>
              ) : (
                <button
                  onClick={() => archive(selected.requirement_id, false)}
                  className="px-2.5 py-1 text-xs rounded bg-rose-600 text-white"
                  data-testid="drawer-archive-btn"
                >
                  {t('provider.reqCatalog.drawerArchive')}
                </button>
              )}
            </div>
          ) : null
        }
      >
        {selected && (
          <div className="space-y-3">
            <DrawerField label={t('provider.reqCatalog.fieldReqId')}>
              <code className="text-[11px] text-slate-700">{selected.requirement_id}</code>
            </DrawerField>
            <DrawerField label={t('provider.reqCatalog.fieldType')}>
              <Pill tone={TYPE_BADGE[selected.type]} label={selected.type} />
            </DrawerField>
            <DrawerField label={t('provider.reqCatalog.fieldDescription')}>
              <p className="text-sm text-slate-700">{selected.description || '—'}</p>
            </DrawerField>
            <DrawerField label={t('provider.reqCatalog.fieldCreated')}>
              <span className="text-xs text-slate-500">{formatDateTimeLocale(selected.created_at)}</span>
            </DrawerField>
            <DrawerField label={t('provider.reqCatalog.fieldLastUpdate')}>
              <span className="text-xs text-slate-500">{formatDateTimeLocale(selected.updated_at)}</span>
            </DrawerField>
            {versionList && (
              <div className="border-t border-slate-200 pt-3" data-testid="version-history">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{t('provider.reqCatalog.versionHistory')}</div>
                <ul className="text-xs space-y-1">
                  {versionList.map((v) => (
                    <li key={v.version} className="flex items-center gap-2">
                      <span className="font-mono w-8">v{v.version}</span>
                      <span className="text-slate-700">{v.title}</span>
                      <span className="text-slate-400 ml-auto">{formatDateTimeLocale(v.frozen_at)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </DetailDrawer>

      {/* Edit / create modal */}
      {draft && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/40 flex items-center justify-center"
          data-testid="catalog-edit-modal"
          onClick={() => setDraft(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-sm font-semibold text-slate-900 mb-3">
              {draft.requirement_id ? t('provider.reqCatalog.modalEdit') : t('provider.reqCatalog.modalNew')}
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">{t('provider.reqCatalog.labelType')}</label>
                <select
                  value={draft.type}
                  onChange={(e) => setDraft({ ...draft, type: e.target.value })}
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                  data-testid="edit-type-select"
                >
                  <option value="DOCUMENT">{t('provider.reqCatalog.typeDocument')}</option>
                  <option value="CONSENT">{t('provider.reqCatalog.typeConsent')}</option>
                  <option value="PATIENT_ACTION">{t('provider.reqCatalog.typePatientAction')}</option>
                  <option value="TASK">{t('provider.reqCatalog.typeTask')}</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">{t('provider.reqCatalog.labelTitle')}</label>
                <input
                  type="text"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                  placeholder={t('provider.reqCatalog.titlePh')}
                  data-testid="edit-title-input"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">{t('provider.reqCatalog.labelDescription')}</label>
                <textarea
                  value={draft.description || ''}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  rows={3}
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                  data-testid="edit-description-input"
                />
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                onClick={() => setDraft(null)}
                className="px-3 py-1.5 text-xs rounded border border-slate-300 hover:bg-slate-50"
                data-testid="edit-cancel-btn"
              >
                {t('provider.reqCatalog.btnCancel')}
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="px-3 py-1.5 text-xs rounded bg-slate-900 text-white disabled:opacity-50"
                data-testid="edit-save-btn"
              >
                {saving ? t('provider.reqCatalog.btnSaving') : draft.requirement_id ? t('provider.reqCatalog.btnSave') : t('provider.reqCatalog.btnCreate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
